import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";

const LOGO = "/logo-geekcreative.png";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [counties, setCounties] = useState([]);
  const [form, setForm] = useState({
    email: "", password: "", first_name: "", last_name: "",
    business_name: "", phone: "", address_line1: "", address_line2: "",
    town: "", county: "", eircode: "", vat_number: ""
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/auth/counties").then((r) => setCounties(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await register(form);
      toast.success("Account created");
      nav("/dashboard");
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  const field = (name, label, opts = {}) => (
    <div>
      <div className="tech-label mb-2">{label}{opts.required !== false && " *"}</div>
      <input value={form[name]} onChange={(e) => set(name, e.target.value)} type={opts.type || "text"} required={opts.required !== false}
        className="gc-input" data-testid={`register-${name}`} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--gc-bg)] text-[var(--gc-navy)]">
      <header className="border-b border-[var(--gc-border)] px-8 h-24 flex items-center bg-white">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO} alt="GeekCreative" className="h-16 w-auto block" />
        </Link>
      </header>
      <div className="paint-stroke" style={{ height: '4px' }} />

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="chip mb-4">// create account</div>
        <h1 className="font-display text-5xl md:text-6xl">Let&apos;s get you set up.</h1>
        <p className="text-[var(--gc-text-muted)] mt-3 text-lg max-w-xl">Tell us about your business so we can tailor quotes and manage every project you send our way.</p>

        {err && <div className="mt-6 border-2 border-[var(--gc-danger)] bg-red-50 text-[var(--gc-danger)] font-semibold p-3 text-sm" data-testid="register-error">{err}</div>}

        <form onSubmit={submit} className="mt-8 space-y-10" data-testid="register-form">
          <section>
            <div className="chip chip-orange mb-4">// personal</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {field("first_name", "First Name")}
              {field("last_name", "Last Name")}
              {field("email", "Email", { type: "email" })}
              {field("password", "Password (min 6)", { type: "password" })}
              {field("phone", "Phone")}
            </div>
          </section>

          <section>
            <div className="chip chip-orange mb-4">// business</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {field("business_name", "Business Name")}
              {field("vat_number", "VAT Number (optional)", { required: false })}
            </div>
          </section>

          <section>
            <div className="chip chip-orange mb-4">// address · ireland</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {field("address_line1", "Address Line 1")}
              {field("address_line2", "Address Line 2", { required: false })}
              {field("town", "Town / City")}
              <div>
                <div className="tech-label mb-2">County *</div>
                <select required value={form.county} onChange={(e) => set("county", e.target.value)}
                  className="gc-input" data-testid="register-county">
                  <option value="">Select county…</option>
                  {counties.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {field("eircode", "Eircode")}
            </div>
          </section>

          <button type="submit" disabled={loading}
            className="btn-primary w-full py-4 inline-flex items-center justify-center gap-2 disabled:opacity-50 text-base"
            data-testid="register-submit">
            {loading ? "Creating account…" : <>Create account <ArrowRight size={18} weight="bold"/></>}
          </button>
          <div className="text-sm text-[var(--gc-text-muted)]">
            Already have an account? <Link to="/login" className="text-[var(--gc-orange)] font-semibold hover:underline" data-testid="register-login-link">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
